// src/hooks/useWeekQuestionnaireButton.ts
import { useEffect, useMemo, useState } from "react";
import { doc, onSnapshot } from "firebase/firestore";
import { db } from "../firebase/config";
import { useAuth } from "../context/AuthContext";
import { useWeekContext } from "../context/WeekContext";
import { useLessons } from "./useLessons"; // ← same as PlayerPage

interface LessonProgress {
  status?: "completed" | "in_progress" | "not_started";
  percentage?: number;
}

export function useWeekQuestionnaireButton(weekNumber: number): {
  showButton: boolean;
  isSubmitted: boolean;
  loading: boolean;
} {
  const { user } = useAuth();
  const { unlockedWeek, loading: lockLoading } = useWeekContext();
  const { weeks, loading: lessonsLoading } = useLessons(); // ← already birth-type filtered

  const [lastLessonProgress, setLastLessonProgress] = useState<LessonProgress | null>(null);
  const [progressLoading, setProgressLoading] = useState(true);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [submittedLoading, setSubmittedLoading] = useState(true);

  // ── Same logic as PlayerPage ─────────────────────────────────────────────
  const lastLessonId = useMemo(() => {
    const lessonsInWeek = weeks[weekNumber] || [];
    if (lessonsInWeek.length === 0) return null;
    return lessonsInWeek[lessonsInWeek.length - 1]?.id?.toUpperCase();
  }, [weeks, weekNumber]);

  // ── Real-time listener on the last lesson's progress doc ─────────────────
  useEffect(() => {
    if (!user?.uid || !lastLessonId) {
      setProgressLoading(false);
      return;
    }

    setProgressLoading(true);
    const progressRef = doc(db, "users", user.uid, "progress", lastLessonId);
    const unsubscribe = onSnapshot(progressRef, (snap) => {
      setLastLessonProgress(snap.exists() ? (snap.data() as LessonProgress) : null);
      setProgressLoading(false);
    });

    return () => unsubscribe();
  }, [user?.uid, lastLessonId]);

  // ── Real-time listener on questionnaire submission ───────────────────────
  useEffect(() => {
    if (!user?.uid || !weekNumber) {
      setSubmittedLoading(false);
      return;
    }

    setSubmittedLoading(true);
    const submittedRef = doc(db, "users", user.uid, "questionnaireSubmitted", `week_${weekNumber}`);
    const unsubscribe = onSnapshot(submittedRef, (snap) => {
      setIsSubmitted(snap.exists());
      setSubmittedLoading(false);
    });

    return () => unsubscribe();
  }, [user?.uid, weekNumber]);

  // ── Show button only when last lesson is completed ───────────────────────
  const showButton = useMemo(() => {
    if (lockLoading || lessonsLoading || progressLoading) return false;

    // Past weeks: always show
    if (weekNumber < unlockedWeek) return true;

    // Current week: only when last lesson is completed
    return (
      lastLessonProgress?.status === "completed" ||
      (lastLessonProgress?.percentage ?? 0) >= 95
    );
  }, [lockLoading, lessonsLoading, progressLoading, weekNumber, unlockedWeek, lastLessonProgress]);

  const loading = submittedLoading || lessonsLoading || progressLoading || lockLoading;

  return { showButton, isSubmitted, loading };
}